import * as React from "react";
import { useMediaQuery } from '@mui/material';
import NextImage, { ImageProps as NextImageProps } from "next/image";
import * as AspectRatio from "@radix-ui/react-aspect-ratio";
import { cn } from "@/src/shared/utils/mergeTwClasses";
import { isString, size } from "lodash";
import {StepAnalytics} from "@/src/shared/models/analytics";
import {useAnalytics} from "@/src/components/pages/StepPage/components/useAnalytics";

interface CustomNextImageProps
  extends Omit<NextImageProps, "src" | "width" | "height"> {
  is: any;
  className?: string;
  aspectRatioWidth?: number;
  aspectRatioHeight?: number;
  width?: string | number;
  height?: string | number;
  style?: React.CSSProperties;
  objectFit?: React.CSSProperties["objectFit"];
  srcMobile?: string;
  sizeMode?: "size" | "aspectRatio";
  src?: string;
  desktopImgClassName?: string;
  mobileImgClassName?: string;
  navigationLink?: string;
  analytics?: StepAnalytics;
}

type WithLinkProps = {
  navigationLink?: string;
  navigationLinkMobile?: string;
};

function withLink<T extends object>(
  WrappedComponent: React.ComponentType<T>,
): React.ComponentType<T & WithLinkProps> {
  return function ComponentWithLink(props: T & WithLinkProps) {
    const { navigationLink, navigationLinkMobile, ...restProps } = props;
    const isMobile = useMediaQuery('(max-width:768px)');
    if (navigationLink && !isMobile) {
      return (
        <a href={navigationLink} target="_blank">
          <WrappedComponent {...(restProps as T)} />
        </a>
      );
    }
    if (navigationLinkMobile && isMobile) {
      return (
        <a href={navigationLinkMobile} target="_blank">
          <WrappedComponent {...(restProps as T)} />
        </a>
      );
    }
    return <WrappedComponent {...(restProps as T)} />;
  };
}

function RawImage(props: CustomNextImageProps) {
  const {
    className,
    aspectRatioWidth = 16,
    aspectRatioHeight = 9,
    width = "100%",
    height = "auto",
    sizeMode = "size",
    style,
    alt = "",
    objectFit = "contain",
    src,
    srcMobile,
    desktopImgClassName,
    mobileImgClassName,
    analytics,
    ...restProps
  } = props;

  const { callAnalyticEvent } = useAnalytics();

  if (sizeMode === "size") {
    return (
      <>
        <div
          style={{ width, height, ...style }}
          className={cn(
            className,
            "overflow-hidden md:hidden",
            mobileImgClassName,
          )}
          onClick={() => {
            analytics && callAnalyticEvent(analytics);
          }}
        >
          <picture>
            {isString(srcMobile) && size(srcMobile) > 0 && (
              <source
                srcSet={srcMobile}
                media="(max-width: 768px)"
                className={cn("h-full w-full", mobileImgClassName)}
              />
            )}
            <NextImage
              src={src || ""}
              alt={alt}
              className={cn("h-full w-full", mobileImgClassName)}
              style={{ objectFit }}
              width={600}
              height={600}
              quality={100}
              {...restProps}
            />
          </picture>
        </div>
        <div
          style={{ width, height, ...style }}
          className={cn(
            className,
            "hidden overflow-hidden md:block",
            desktopImgClassName,
          )}
          onClick={() => {
            analytics && callAnalyticEvent(analytics);
          }}
        >
          <picture>
            {isString(srcMobile) && size(srcMobile) > 0 && (
              <source
                srcSet={srcMobile}
                media="(max-width: 768px)"
                className={cn("h-full w-full", desktopImgClassName)}
              />
            )}
            <NextImage
              src={src || ""}
              alt={alt}
              className={cn("h-full w-full", desktopImgClassName)}
              style={{ objectFit }}
              width={1440}
              height={1440}
              quality={100}
              {...restProps}
            />
          </picture>
        </div>
      </>
    );
  }

  return (
    <AspectRatio.Root
      ratio={aspectRatioWidth / aspectRatioHeight}
      className={cn(className, "overflow-hidden")}
      style={style}
    >
      <NextImage
        src={src || ""}
        alt={alt}
        className="h-full w-full"
        style={{ objectFit }}
        quality={100}
        width={1440}
        height={1440}
        {...restProps}
      />
    </AspectRatio.Root>
  );
}

const Image = withLink(RawImage);

export { Image };
